import React, { useRef, useState } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { staticImageAssets } from 'js/constants/Paths';
import AnimationClouds from './Animation.clouds';

import { ANIMTION_BALLOON } from 'js/constants/Animations';
import { useSFX } from 'js/hooks/useSFX';

// import tracking
import { track, TRACKING_EVENTS } from 'js/utils/tracking';

gsap.registerPlugin(useGSAP);

const AnimationBalloon = ({ popcornAnimationComplete }) => {
  const { playSFX } = useSFX();
  const dragContainer = useRef(null);
  const tapElement = useRef(null);
  const textRef = useRef(null);
  const [showText, updateShowText] = useState(false);

  useGSAP(
    () => {
      const el = tapElement.current;

      if (!el || !popcornAnimationComplete) return;

      let x = 0;
      let y = 0;
      let vx = 0;
      let vy = 0;
      let rotation = 0;
      let physicsActive = false;
      let canInteract = false;

      const clamp = gsap.utils.clamp;

      let introTween;
      let idleTween;
      let pulseTween;
      let lastPromptCycle = -1;

      const syncFromElement = () => {
        x = gsap.getProperty(el, 'x');
        y = gsap.getProperty(el, 'y');
        rotation = gsap.getProperty(el, 'rotation');
      };

      const resetVelocity = () => {
        vx = 0;
        vy = 0;
      };

      const respawn = () => {
        gsap.ticker.remove(update);
        physicsActive = false;
        x = 0;
        y = 0;
        rotation = 0;

        resetVelocity();
        playSFX('giggle');

        gsap.set(el, {
          x,
          y,
          rotation,
          scale: 0,
          onComplete: () => {
            gsap.to(el, {
              scale: 1,
              onComplete: () => {
                setIdleTween();
              },
            });
          },
        });
      };

      const isOffScreen = () => {
        const rect = el.getBoundingClientRect();

        return rect.left > window.innerWidth || rect.right < 0 || rect.top > window.innerHeight || rect.bottom < 0;
      };

      const checkTextOverlap = () => {
        const balloon = el.getBoundingClientRect();
        const text = textRef.current?.getBoundingClientRect();

        if (!text) return;

        const centerY = balloon.top + balloon.height / 2;
        const overlaps = centerY <= text.top;

        if (overlaps) {
          updateShowText(true);
        }
      };

      const setIdleTween = () => {
        syncFromElement();
        idleTween?.kill();
        pulseTween?.kill();
        lastPromptCycle = -1;
        idleTween = gsap.to(tapElement.current, {
          y: -10,
          duration: 2.8,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
          onUpdate: () => {
            const elapsed = idleTween?.totalTime() || 0;
            const promptCycle = Math.floor(elapsed / 10);
            const promptWindowStarted = elapsed % 10 >= 9;

            if (!promptWindowStarted || promptCycle === lastPromptCycle) return;

            lastPromptCycle = promptCycle;
            pulseTween?.kill();
            pulseTween = gsap.fromTo(
              tapElement.current,
              { scale: 1 },
              {
                scale: 1.1,
                duration: 0.75,
                ease: 'sine.inOut',
                repeat: 3,
                yoyo: true,
                overwrite: 'auto',
                onComplete: () => gsap.to(tapElement.current, { scale: 1, duration: 0.75 }),
              }
            );
          },
        });
      };

      const update = () => {
        if (!physicsActive) return;

        const delta = gsap.ticker.deltaRatio(60);

        vy += ANIMTION_BALLOON.GRAVITY * delta;

        vx *= Math.pow(ANIMTION_BALLOON.AIR_RESISTANCE, delta);
        vy *= Math.pow(ANIMTION_BALLOON.AIR_RESISTANCE, delta);

        x += vx * delta;
        y += vy * delta;

        vx = clamp(-ANIMTION_BALLOON.MAX_VELOCITY_X, ANIMTION_BALLOON.MAX_VELOCITY_X, vx);
        vy = clamp(-ANIMTION_BALLOON.MAX_VELOCITY_Y, ANIMTION_BALLOON.MAX_VELOCITY_Y, vy);

        gsap.set(el, {
          x,
          y,
          rotation,
        });

        checkTextOverlap();

        if (isOffScreen()) {
          respawn();
        }
      };

      const handleTap = (event) => {
        if (!canInteract) return;

        playSFX('bop');
        track(TRACKING_EVENTS.CLICK_ACTION_BALLOON.id);

        if (!physicsActive) {
          idleTween?.kill();
          pulseTween?.kill();
          gsap.to(el, { scale: 1, duration: 0.75 });
          syncFromElement();
          gsap.ticker.add(update);
          physicsActive = true;
          return;
        }

        const rect = el.getBoundingClientRect();

        const tapX = event.clientX - rect.left;
        const tapY = event.clientY - rect.top;

        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        const offsetX = tapX - centerX;
        const offsetY = tapY - centerY;

        const normalizedX = offsetX / centerX;
        const normalizedY = offsetY / centerY;

        vy -= ANIMTION_BALLOON.UPWARD_FORCE;
        vx -= normalizedX * ANIMTION_BALLOON.SLIDE_FORCE;

        if (normalizedY > 0) {
          vy -= normalizedY * 4;
        }

        vx = clamp(-ANIMTION_BALLOON.MAX_VELOCITY_X, ANIMTION_BALLOON.MAX_VELOCITY_X, vx);
        vy = clamp(-ANIMTION_BALLOON.MAX_VELOCITY_Y, ANIMTION_BALLOON.MAX_VELOCITY_Y, vy);
      };

      const start = () => {
        setIdleTween();
        resetVelocity();

        canInteract = true;
      };

      const floatUpFromBottom = () => {
        physicsActive = false;
        gsap.ticker.remove(update);

        introTween = gsap.fromTo(
          el,
          {
            y: '100vh',
            x: 0,
            rotation: 0,
          },
          {
            y: 0,
            x: 0,
            rotation: 0,
            duration: 10,
            ease: 'elastic.out(1, 1)',
            onUpdate: checkTextOverlap,
            onComplete: start,
          }
        );
      };

      el.addEventListener('pointerdown', handleTap);

      gsap.set(el, {
        x: 0,
        y: '100vh',
        rotation: 0,
        transformOrigin: '50% 50%',
        touchAction: 'none',
        userSelect: 'none',
      });

      floatUpFromBottom();

      return () => {
        idleTween?.kill();
        pulseTween?.kill();

        introTween?.kill();
        gsap.ticker.remove(update);
        el.removeEventListener('pointerdown', handleTap);
        gsap.killTweensOf(el);
      };
    },
    { scope: dragContainer, dependencies: [popcornAnimationComplete] }
  );

  return (
    <>
      {popcornAnimationComplete && <AnimationClouds active={showText} />}

      <div className="animation__balloon" ref={dragContainer}>
        <div
          className="animation__balloon__logo"
          style={{
            backgroundImage: `url(${staticImageAssets('images/static/bluey_logo_center.png')})`,
          }}
        />

        {popcornAnimationComplete && (
          <div className="animation__balloon__strapline">
            <img
              src={staticImageAssets('images/static/strapline.png')}
              className="animation__balloon__strapline"
              ref={textRef}
            />
          </div>
        )}

        <div
          className="animation__balloon__drag-container"
          ref={tapElement}
          style={{
            backgroundImage: popcornAnimationComplete
              ? `url(${staticImageAssets('images/static/balloon.png')})`
              : 'none',
            touchAction: 'none',
            userSelect: 'none',
            cursor: 'pointer',
          }}
        />
      </div>
    </>
  );
};

export default AnimationBalloon;
